import { useState, type SelectHTMLAttributes, type CSSProperties, type FocusEvent } from "react";
import { useEmbedTokens, useReducedMotion, motionSafe } from "../theme";

interface Props extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "style"> { invalid?: boolean; }

export function EmbedSelect({ invalid = false, disabled, onFocus, onBlur, children, ...rest }: Props) {
  const t = useEmbedTokens();
  const reduced = useReducedMotion();
  const [focused, setFocused] = useState(false);
  const borderColor = invalid ? t.colors.error : focused ? t.colors.borderFocus : t.colors.border;
  const style: CSSProperties = {
    width: "100%", fontFamily: "inherit", fontSize: t.inputFontSize, color: t.colors.text,
    height: t.buttonHeight, padding: `0 ${t.inputPadding}px`, paddingRight: 36,
    borderRadius: t.borderRadius,
    background: disabled ? t.colors.disabledBg : t.colors.inputBg, border: `1px solid ${borderColor}`,
    outline: "none", boxShadow: focused && !invalid ? `0 0 0 3px ${t.colors.primaryTint}` : "none",
    // WebKit includes a closed select's INTRINSIC content width (its longest
    // option's text) in the page's scrollable overflow while the select
    // computes overflow: visible — measured 2026-10-04: at 390px with a
    // 48-char store option, the select's border box was 220px yet hiding it
    // dropped document scrollWidth 449 -> 390 (Chromium never counted it).
    // Hidden clips that contribution; the element's own focus box-shadow is
    // painted by itself and is NOT clipped by its own overflow.
    overflow: "hidden",
    transition: motionSafe(reduced, "border-color .12s, box-shadow .12s"), boxSizing: "border-box",
    appearance: "none",
    backgroundImage:
      "linear-gradient(45deg, transparent 50%, " + t.colors.textSecondary + " 50%), " +
      "linear-gradient(135deg, " + t.colors.textSecondary + " 50%, transparent 50%)",
    backgroundPosition: "calc(100% - 18px) 50%, calc(100% - 13px) 50%",
    backgroundSize: "5px 5px, 5px 5px",
    backgroundRepeat: "no-repeat",
  };
  return (
    <select {...rest} disabled={disabled} style={style}
      onFocus={(e: FocusEvent<HTMLSelectElement>) => { setFocused(true); onFocus?.(e); }}
      onBlur={(e: FocusEvent<HTMLSelectElement>) => { setFocused(false); onBlur?.(e); }}>
      {children}
    </select>
  );
}
